declare global {
  interface Window {
    ml___setOptOutState?: (optOut: number) => void
  }
}

import { useEffect, useRef, useState } from 'react'

import env from '@beam-australia/react-env'
import type {
  ComponentInteractionEventProps,
  EventPayloadWithCustomProps,
} from '@knauf-group/ct-shared-nextjs/app-router/web/analytics/types'
import { useOneTrust } from '@knauf-group/ct-shared-nextjs/app-router/web/hooks/useOneTrust'
import {
  useClickEvent,
  useContactEvent,
} from '@knauf-group/ct-shared-nextjs/app-router/web/hooks/useWebAnalytics'

type MetaLocatorApplicationType = 'Where to Buy' | 'Find my Rep'
type CmsComponent = 'dealer_locator' | 'find_my_rep'

const getCmsComponent = (applicationType?: MetaLocatorApplicationType): CmsComponent => {
  return applicationType === 'Find my Rep' ? 'find_my_rep' : 'dealer_locator'
}

const PAGE_CATEGORY = 'cms_page'
const READY_EVENT_PREFIX = 'metalocator:ready:'

type UseMetaLocatorScriptProps = {
  itemId?: string
  applicationType?: MetaLocatorApplicationType
}

interface MetaLocatorPayload {
  id: string
  name: string
  value: string
}

const metaLocatorMessageTypes = [
  'NAVIGATED',
  'EMAIL',
  'PHONE',
  'COPY_PHONE',
  'COPY_ADDRESS',
  'COPY_EMAIL',
] as const

type MetaLocatorMessage = {
  type: (typeof metaLocatorMessageTypes)[number]
  payload: MetaLocatorPayload
}

const isMetaLocatorMessage = (data: unknown): data is MetaLocatorMessage => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    metaLocatorMessageTypes.includes((data as any).type) &&
    'payload' in data &&
    typeof (data as any).payload === 'object' &&
    (data as any).payload !== null &&
    (data as any).payload.id &&
    (data as any).payload.name &&
    (data as any).payload.value
  )
}

const commonEventPayload = (
  payload: MetaLocatorPayload,
  cmsComponent: CmsComponent,
): EventPayloadWithCustomProps => ({
  action: payload['value'],
  page_section: 'main',
  event_specification: 'external_link_click',
  cms_component: cmsComponent,
  page_category: PAGE_CATEGORY,
  target: `${payload['id']}-${payload['name']}`,
})

const commonComponentInteraction = (
  payload: MetaLocatorPayload,
  cmsComponent: CmsComponent,
): ComponentInteractionEventProps => ({
  action: 'button',
  target: `${payload['id']}-${payload['name']}`,
  page_category: PAGE_CATEGORY,
  event_specification: 'confirm_button',
  cms_component: cmsComponent,
})

/**
 * React hook to load and manage the MetaLocator widget.
 *
 * Notes:
 * - This is used for both "Where to Buy" and "Find my Rep" flows.
 * - Tracks consent via OneTrust and updates MetaLocator opt-out accordingly.
 */
const useMetaLocatorScript = ({ itemId, applicationType }: UseMetaLocatorScriptProps) => {
  const [isScriptReady, setIsScriptReady] = useState(false)
  const { push: pushClick, pushComponentInteraction } = useClickEvent()
  const { push: pushContact } = useContactEvent()
  const { isFunctionalCookieSet, isPerformanceCookieSet } = useOneTrust()
  const pushClickRef = useRef(pushClick)
  const pushComponentInteractionRef = useRef(pushComponentInteraction)
  const pushContactRef = useRef(pushContact)
  const cmsComponentRef = useRef<CmsComponent>(getCmsComponent(applicationType))
  const metaLocatorBaseUrl = env('METALOCATOR_BASE_URL') || 'https://code.metalocator.com'
  const normalizedMetaLocatorBaseUrl = metaLocatorBaseUrl.replace(/\/+$/, '') // Remove trailing slashes in case there are any.

  useEffect(() => {
    pushClickRef.current = pushClick
    pushComponentInteractionRef.current = pushComponentInteraction
    pushContactRef.current = pushContact
  }, [pushClick, pushComponentInteraction, pushContact])

  useEffect(() => {
    cmsComponentRef.current = getCmsComponent(applicationType)
  }, [applicationType])

  // ─── OneTrust consent management ──────────────────────────────────────────
  // Update MetaLocator opt-out state whenever consent changes.
  // Wait to execute until the script has fully initialized and injected its DOM.
  useEffect(() => {
    if (!itemId) return

    if (isScriptReady && typeof window.ml___setOptOutState === 'function') {
      const optOut = isFunctionalCookieSet && isPerformanceCookieSet ? 0 : 1
      window.ml___setOptOutState(optOut)
    }
  }, [itemId, isFunctionalCookieSet, isPerformanceCookieSet, isScriptReady])

  useEffect(() => {
    if (!itemId) {
      return
    }

    setIsScriptReady(false)
    // ─── OneTrust: initial opt-out state ────────────────────────────────────
    // Set MetaLocator's initial tracking state based on current consent.
    // Returning users who already consented get tracking from the start;
    // new users default to opt-out until consent is given.
    const initialOptOut = isFunctionalCookieSet && isPerformanceCookieSet ? 0 : 1
    const paramsScript = document.createElement('script')
    paramsScript.type = 'text/javascript'
    paramsScript.innerHTML = `var ml___params = { _opt_out: ${initialOptOut} };`
    document.head.appendChild(paramsScript)

    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.innerHTML = `
      (function() {
        var container = document.querySelector('[data-metalocator="locator"]');
        if (!container) return;

        var itemId = container.getAttribute('data-metalocator-itemid');
        var div = document.createElement('div');
        div.id = '___ml_container' + itemId;
        container.appendChild(div);

        var originalWrite = document.write, buffer = [];
        var timeoutId, fallbackTimeoutId, isFinalized = false;

        var finalize = function() {
          if (isFinalized) return;
          isFinalized = true;
          observer.disconnect();
          clearTimeout(timeoutId);
          clearTimeout(fallbackTimeoutId);
          document.write = originalWrite;
          var el = document.getElementById('locator_loading' + itemId);
          if (el) el.style.display = 'none';
          window.dispatchEvent(new CustomEvent('${READY_EVENT_PREFIX}' + itemId));
        };

        document.write = function(content) {
          clearTimeout(timeoutId);
          buffer.push(content);
          timeoutId = setTimeout(function() {
            div.innerHTML = buffer.join('');
            document.write = originalWrite;
          }, 100);
        };

        var s = document.createElement('script');
        s.setAttribute('data-ot-ignore', '');
        s.src = ${JSON.stringify(
          normalizedMetaLocatorBaseUrl,
        )} + '/index.php?option=com_locator&view=directory&layout=_javascript&framed=1&format=raw&tmpl=component&no_html=1&Itemid=' + itemId;
        div.appendChild(s);

        var observer = new MutationObserver(function() {
          if (isFinalized) return;
          if (div.innerHTML.length > 500 || div.querySelector('iframe, form, .ml-map, #map')) {
            finalize();
          }
        });

        observer.observe(div, { childList: true, subtree: true });
        fallbackTimeoutId = setTimeout(finalize, 15000);
        window['ml___cleanup_' + itemId] = finalize;
      })();
    `
    document.body.appendChild(script)

    const handleScriptReady = () => setIsScriptReady(true)
    const eventName = `${READY_EVENT_PREFIX}${itemId}`
    window.addEventListener(eventName, handleScriptReady)

    const handleMetaLocatorAnalyticsEvents = (event: MessageEvent) => {
      if (event.origin !== normalizedMetaLocatorBaseUrl) return
      if (!isMetaLocatorMessage(event.data)) return

      const { type, payload } = event.data
      const cmsComponent = cmsComponentRef.current

      switch (type) {
        case 'NAVIGATED': {
          pushClickRef.current({
            reference: { href: 'navigation', label: 'Navigated' },
            type: 'link',
            eventPayload: {
              ...commonEventPayload(payload, cmsComponent),
              action: payload['value'],
            },
          })
          break
        }
        case 'EMAIL': {
          pushContactRef.current({
            reference: { href: 'email', label: 'Email' },
            type: 'link',
            eventPayload: {
              ...commonEventPayload(payload, cmsComponent),
              action: payload['value'],
            },
          })
          break
        }
        case 'PHONE': {
          pushContactRef.current({
            reference: { href: 'phone', label: 'PhoneCall' },
            type: 'link',
            eventPayload: {
              ...commonEventPayload(payload, cmsComponent),
              action: payload['value'],
            },
          })
          break
        }
        case 'COPY_PHONE': {
          pushComponentInteractionRef.current(commonComponentInteraction(payload, cmsComponent))
          break
        }
        case 'COPY_ADDRESS': {
          pushComponentInteractionRef.current(commonComponentInteraction(payload, cmsComponent))
          break
        }
        case 'COPY_EMAIL': {
          pushComponentInteractionRef.current(commonComponentInteraction(payload, cmsComponent))
          break
        }
      }
    }

    globalThis.window.addEventListener('message', handleMetaLocatorAnalyticsEvents)

    return () => {
      window.removeEventListener(eventName, handleScriptReady)
      window.removeEventListener('message', handleMetaLocatorAnalyticsEvents)
      document.body.removeChild(script)
      if (document.head.contains(paramsScript)) document.head.removeChild(paramsScript)
      const key = 'ml___cleanup_' + itemId
      const windowAny = globalThis.window as any
      if (windowAny[key]) {
        windowAny[key]()
        delete windowAny[key]
      }
    }
  }, [itemId, normalizedMetaLocatorBaseUrl])
}

export default useMetaLocatorScript
